<template>
  <div class="input def-textarea">
    <label
      :for="inputId"
      :id="labelId"
      class="mb-2 input-label text-left"
      :class="{ error: !!error }"
    >
      <template v-if="icon">
        <font-awesome-icon :icon="icon" :style="{ width: '24px', color: iconColor }" />
      </template>
      {{ label }}<span v-if="required" aria-hidden="true">*</span>
    </label>
    <textarea
      v-bind="inputAttrs"
      :id="inputId"
      :value="normalizedValue"
      :placeholder="placeholder"
      :aria-required="required ? 'true' : null"
      :aria-invalid="error ? 'true' : null"
      :aria-describedby="describedBy"
      class="input-field"
      @input="onInput"
      :autocomplete="textAreaAutocomplete"
      rows="4"
      :maxlength="maxLength"
    />
    <div class="textarea-counter" aria-hidden="true">{{ characterCount }}/{{ maxLength }}</div>
    <div v-if="error" :id="errorId" class="input-error" role="alert" aria-live="assertive">
      <font-awesome-icon :icon="['fas', 'circle-exclamation']" /> {{ error }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'TextArea',
  inheritAttrs: false,
  props: {
    value: { type: String, default: '' },
    label: { type: String, required: true },
    placeholder: { type: String, default: '' },
    error: { type: String, default: '' },
    required: { type: Boolean, default: false },
    id: { type: String, default: null },
    maxLength: { type: Number, default: 500 },
    icon: { type: String, default: null },
    iconColor: { type: String, default: null },
  },
  computed: {
    inputId() {
      return this.id || `textarea-${this._uid}`;
    },
    labelId() {
      return `${this.inputId}-label`;
    },
    errorId() {
      return `${this.inputId}-error`;
    },
    normalizedValue() {
      return this.value || '';
    },
    characterCount() {
      return this.normalizedValue.length;
    },
    describedBy() {
      const ids = [];
      if (this.$attrs['aria-describedby']) {
        ids.push(this.$attrs['aria-describedby']);
      }
      if (this.error) {
        ids.push(this.errorId);
      }
      return ids.join(' ') || null;
    },
    inputAttrs() {
      const attrs = { ...this.$attrs };
      delete attrs['aria-label'];
      delete attrs['aria-describedby'];
      delete attrs['aria-invalid'];
      delete attrs.autocomplete;
      return attrs;
    },
    textAreaAutocomplete() {
      return this.$attrs.autocomplete !== undefined ? this.$attrs.autocomplete : 'off';
    },
  },
  methods: {
    onInput(event) {
      let val = event.target.value;
      if (val.length > this.maxLength) val = val.slice(0, this.maxLength);
      this.$emit('input', val);
    }
  }
};
</script>

<style scoped>
.input {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}
.input-label {
  margin-bottom: 0.25rem;
}
.input-field {
  padding: 0.5rem;
  text-align: left;
  resize: vertical;
}
.input-error {
  margin-top: 0.25rem;
  text-align: left;
}
.textarea-counter {
  text-align: right;
  margin-top: 0.1rem;
}
</style>
